import React, {useEffect, useState, useRef, useCallback} from "react";
import ReactDOM from "react-dom";
import {FormattedMessage, useIntl} from "react-intl";
import Icon from "../Icon";
import classes from "./styles.module.css";

var MOBILE_BREAKPOINT = 767;

var MONTH_NAMES_RU = [
    "январь", "февраль", "март", "апрель", "май", "июнь",
    "июль", "август", "сентябрь", "октябрь", "ноябрь", "декабрь"
];

var MONTH_NAMES_EN = [
    "January", "February", "March", "April", "May", "June",
    "July", "August", "September", "October", "November", "December"
];

var DAY_NAMES_RU = ["пн", "вт", "ср", "чт", "пт", "сб", "вс"];
var DAY_NAMES_EN = ["Mo", "Tu", "We", "Th", "Fr", "Sa", "Su"];

function pad(n) {
    return n < 10 ? "0" + n : "" + n;
}

function formatDateShort(date) {
    if (!date) return "";
    return pad(date.getDate()) + "." + pad(date.getMonth() + 1) + "." + date.getFullYear();
}

function formatTime(date) {
    if (!date) return "00:00";
    return pad(date.getHours()) + ":" + pad(date.getMinutes());
}

function isSameDay(a, b) {
    if (!a || !b) return false;
    return a.getFullYear() === b.getFullYear() &&
        a.getMonth() === b.getMonth() &&
        a.getDate() === b.getDate();
}

function isInRange(day, start, end) {
    if (!start || !end) return false;
    var t = day.getTime();
    var s = new Date(start.getFullYear(), start.getMonth(), start.getDate()).getTime();
    var e = new Date(end.getFullYear(), end.getMonth(), end.getDate()).getTime();
    return t >= s && t <= e;
}

function getDaysInMonth(year, month) {
    return new Date(year, month + 1, 0).getDate();
}

function getFirstDayOfWeek(year, month) {
    var d = new Date(year, month, 1).getDay();
    return d === 0 ? 6 : d - 1;
}

function buildCalendarGrid(year, month) {
    var daysInMonth = getDaysInMonth(year, month);
    var firstDay = getFirstDayOfWeek(year, month);
    var prevMonth = month === 0 ? 11 : month - 1;
    var prevYear = month === 0 ? year - 1 : year;
    var daysInPrev = getDaysInMonth(prevYear, prevMonth);

    var cells = [];

    for (var i = firstDay - 1; i >= 0; i--) {
        cells.push({
            day: daysInPrev - i,
            month: prevMonth,
            year: prevYear,
            outside: true
        });
    }

    for (var d = 1; d <= daysInMonth; d++) {
        cells.push({day: d, month: month, year: year, outside: false});
    }

    var remaining = 42 - cells.length;
    var nextMonth = month === 11 ? 0 : month + 1;
    var nextYear = month === 11 ? year + 1 : year;
    for (var j = 1; j <= remaining; j++) {
        cells.push({day: j, month: nextMonth, year: nextYear, outside: true});
    }

    return cells;
}

function DateRange(props) {
    var onSelectDate = props.onSelectDate;
    var defaultPeriod = props.defaultPeriod;

    var intl = useIntl();
    var isRu = intl.locale === "ru";
    var monthNames = isRu ? MONTH_NAMES_RU : MONTH_NAMES_EN;
    var dayNames = isRu ? DAY_NAMES_RU : DAY_NAMES_EN;

    var _open = useState(false);
    var isOpen = _open[0];
    var setIsOpen = _open[1];

    var _startDate = useState(null);
    var startDate = _startDate[0];
    var setStartDate = _startDate[1];

    var _endDate = useState(null);
    var endDate = _endDate[0];
    var setEndDate = _endDate[1];

    var _hoverDate = useState(null);
    var hoverDate = _hoverDate[0];
    var setHoverDate = _hoverDate[1];

    var _selectingEnd = useState(false);
    var selectingEnd = _selectingEnd[0];
    var setSelectingEnd = _selectingEnd[1];

    var _rightMonth = useState(function () {
        var now = new Date();
        return {year: now.getFullYear(), month: now.getMonth()};
    });
    var rightMonth = _rightMonth[0];
    var setRightMonth = _rightMonth[1];

    var _startTime = useState("00:00");
    var startTime = _startTime[0];
    var setStartTime = _startTime[1];

    var _endTime = useState("23:59");
    var endTime = _endTime[0];
    var setEndTime = _endTime[1];

    var wrapRef = useRef(null);
    var triggerRef = useRef(null);
    var dropdownRef = useRef(null);
    var initRef = useRef(false);

    var _dropdownPos = useState({top: 0, left: 0});
    var dropdownPos = _dropdownPos[0];
    var setDropdownPos = _dropdownPos[1];

    var _isMobile = useState(function () {
        return typeof window !== "undefined" && window.innerWidth <= MOBILE_BREAKPOINT;
    });
    var isMobile = _isMobile[0];
    var setIsMobile = _isMobile[1];

    useEffect(function () {
        function handleResize() {
            setIsMobile(window.innerWidth <= MOBILE_BREAKPOINT);
        }
        window.addEventListener("resize", handleResize);
        return function () {
            window.removeEventListener("resize", handleResize);
        };
    }, []);

    function getLeftMonth() {
        var m = rightMonth.month - 1;
        var y = rightMonth.year;
        if (m < 0) { m = 11; y--; }
        return {year: y, month: m};
    }

    function prevMonth() {
        setRightMonth(function (prev) {
            var m = prev.month - 1;
            var y = prev.year;
            if (m < 0) { m = 11; y--; }
            return {year: y, month: m};
        });
    }

    function nextMonth() {
        setRightMonth(function (prev) {
            var m = prev.month + 1;
            var y = prev.year;
            if (m > 11) { m = 0; y++; }
            return {year: y, month: m};
        });
    }

    function sendFormatDate(start, end) {
        if (!start || !end) {
            onSelectDate({startDate: null, endDate: null});
            return;
        }

        var options = {
            day: "numeric",
            month: "numeric",
            year: "numeric",
            hour: "numeric",
            minute: "numeric",
            timeZoneName: "short",
        };

        var startStr = start.toLocaleDateString("ru", options);
        var endStr = end.toLocaleDateString("ru", options);

        onSelectDate({
            startDate: startStr,
            endDate: endStr,
        });
    }

    function applyTimeToDate(date, timeStr) {
        if (!date) return null;
        var parts = timeStr.split(":");
        var h = parseInt(parts[0], 10) || 0;
        var m = parseInt(parts[1], 10) || 0;
        var d = new Date(date);
        d.setHours(h, m, 0, 0);
        return d;
    }

    function handleDayClick(cell) {
        if (!selectingEnd) {
            var newStart = new Date(cell.year, cell.month, cell.day);
            newStart = applyTimeToDate(newStart, startTime);
            setStartDate(newStart);
            setEndDate(null);
            setSelectingEnd(true);
        } else {
            var clickedDate = new Date(cell.year, cell.month, cell.day);
            var startOnly = new Date(startDate.getFullYear(), startDate.getMonth(), startDate.getDate());

            if (clickedDate < startOnly) {
                var newS = applyTimeToDate(clickedDate, startTime);
                setStartDate(newS);
                setSelectingEnd(true);
                return;
            }

            var newEnd = applyTimeToDate(clickedDate, endTime);
            setEndDate(newEnd);
            setSelectingEnd(false);

            var finalStart = applyTimeToDate(startDate, startTime);
            sendFormatDate(finalStart, newEnd);
        }
    }

    function handleDayHover(cell) {
        if (selectingEnd) {
            setHoverDate(new Date(cell.year, cell.month, cell.day));
        }
    }

    function setPreset(presetFn) {
        presetFn();
        setSelectingEnd(false);
        setIsOpen(false);
    }

    var setToday = useCallback(function () {
        var start = new Date();
        start.setHours(0, 0, 0, 0);
        var end = new Date();
        end.setHours(23, 59, 59, 999);
        setStartDate(start);
        setEndDate(end);
        setStartTime("00:00");
        setEndTime("23:59");
        sendFormatDate(start, end);
    }, []);

    var setYesterday = useCallback(function () {
        var yesterday = new Date();
        yesterday.setDate(yesterday.getDate() - 1);
        var start = new Date(yesterday);
        start.setHours(0, 0, 0, 0);
        var end = new Date(yesterday);
        end.setHours(23, 59, 59, 999);
        setStartDate(start);
        setEndDate(end);
        setStartTime("00:00");
        setEndTime("23:59");
        sendFormatDate(start, end);
    }, []);

    var setWeek = useCallback(function () {
        var start = new Date();
        start.setDate(start.getDate() - 7);
        start.setHours(0, 0, 0, 0);
        var end = new Date();
        end.setHours(23, 59, 59, 999);
        setStartDate(start);
        setEndDate(end);
        setStartTime("00:00");
        setEndTime("23:59");
        sendFormatDate(start, end);
    }, []);

    var setMonth = useCallback(function () {
        var start = new Date();
        start.setDate(start.getDate() - 30);
        start.setHours(0, 0, 0, 0);
        var end = new Date();
        end.setHours(23, 59, 59, 999);
        setStartDate(start);
        setEndDate(end);
        setStartTime("00:00");
        setEndTime("23:59");
        sendFormatDate(start, end);
    }, []);

    var setHalfYear = useCallback(function () {
        var start = new Date();
        start.setDate(start.getDate() - 180);
        start.setHours(0, 0, 0, 0);
        var end = new Date();
        end.setHours(23, 59, 59, 999);
        setStartDate(start);
        setEndDate(end);
        setStartTime("00:00");
        setEndTime("23:59");
        sendFormatDate(start, end);
    }, []);

    var setZero = useCallback(function () {
        setStartDate(null);
        setEndDate(null);
        setStartTime("00:00");
        setEndTime("23:59");
        sendFormatDate(null, null);
    }, []);

    useEffect(function () {
        if (initRef.current) return;
        initRef.current = true;

        if (defaultPeriod === null) return;
        if (!defaultPeriod) { setWeek(); return; }

        switch (defaultPeriod) {
            case "today": setToday(); break;
            case "yesterday": setYesterday(); break;
            case "week": setWeek(); break;
            case "month": setMonth(); break;
            case "halfYear": setHalfYear(); break;
            case "zero": setZero(); break;
            default: setWeek();
        }
    }, [defaultPeriod]);

    useEffect(function () {
        function handleClickOutside(e) {
            var inWrap = wrapRef.current && wrapRef.current.contains(e.target);
            var inDropdown = dropdownRef.current && dropdownRef.current.contains(e.target);
            if (!inWrap && !inDropdown) {
                setIsOpen(false);
                setSelectingEnd(false);
            }
        }
        document.addEventListener("mousedown", handleClickOutside);
        return function () {
            document.removeEventListener("mousedown", handleClickOutside);
        };
    }, []);

    function handleStartTimeChange(e) {
        var val = e.target.value;
        setStartTime(val);
        if (startDate && endDate) {
            var newStart = applyTimeToDate(startDate, val);
            setStartDate(newStart);
            sendFormatDate(newStart, endDate);
        }
    }

    function handleEndTimeChange(e) {
        var val = e.target.value;
        setEndTime(val);
        if (startDate && endDate) {
            var newEnd = applyTimeToDate(endDate, val);
            setEndDate(newEnd);
            sendFormatDate(startDate, newEnd);
        }
    }

    function renderMonth(yearMonth, showLeftArrow, showRightArrow) {
        var year = yearMonth.year;
        var month = yearMonth.month;
        var grid = buildCalendarGrid(year, month);
        var weeks = [];
        for (var w = 0; w < 6; w++) {
            weeks.push(grid.slice(w * 7, (w + 1) * 7));
        }

        var rangeEnd = selectingEnd && hoverDate ? hoverDate : endDate;

        return (
            <div className={classes.MonthPanel}>
                <div className={classes.MonthHeader}>
                    {showLeftArrow ? (
                        <button className={classes.NavBtn} onClick={prevMonth}>←</button>
                    ) : <span className={classes.NavBtn}/>}
                    <span className={classes.MonthTitle}>
                        {monthNames[month]} {year}
                    </span>
                    {showRightArrow ? (
                        <button className={classes.NavBtn} onClick={nextMonth}>→</button>
                    ) : <span className={classes.NavBtn}/>}
                </div>
                <div className={classes.DayNames}>
                    {dayNames.map(function (name, i) {
                        return (
                            <span key={i} className={i >= 5 ? classes.Weekend : ""}>
                                {name}
                            </span>
                        );
                    })}
                </div>
                <div className={classes.DaysGrid}>
                    {weeks.map(function (week, wi) {
                        return week.map(function (cell, di) {
                            var cellDate = new Date(cell.year, cell.month, cell.day);
                            var isStart = startDate && isSameDay(cellDate, startDate);
                            var isEnd = rangeEnd && isSameDay(cellDate, rangeEnd);
                            var inRange = isInRange(cellDate, startDate, rangeEnd) && !isStart && !isEnd;

                            var cellClasses = classes.Day;
                            if (cell.outside) cellClasses += " " + classes.Outside;
                            if (isStart || isEnd) cellClasses += " " + classes.Selected;
                            if (inRange) cellClasses += " " + classes.InRange;
                            if (isStart && rangeEnd) cellClasses += " " + classes.RangeStart;
                            if (isEnd && startDate) cellClasses += " " + classes.RangeEnd;

                            return (
                                <span
                                    key={wi + "-" + di}
                                    className={cellClasses}
                                    onClick={function () { handleDayClick(cell); }}
                                    onMouseEnter={function () { handleDayHover(cell); }}
                                >
                                    {cell.day}
                                </span>
                            );
                        });
                    })}
                </div>
            </div>
        );
    }

    var displayText = "";
    if (startDate && endDate) {
        displayText = formatDateShort(startDate) + ", " + formatTime(startDate) +
            "  —  " +
            formatDateShort(endDate) + ", " + formatTime(endDate);
    }

    var left = getLeftMonth();

    function closePanel() {
        setIsOpen(false);
        setSelectingEnd(false);
    }

    var panelBody = (
        <React.Fragment>
            <div className={classes.Sidebar}>
                <div className={classes.SidebarTitle}>
                    <FormattedMessage id="DateRange.quickSelect" defaultMessage="Быстрый выбор"/>
                </div>
                <button className={classes.PresetBtn} onClick={function () { setPreset(setToday); }}>
                    <FormattedMessage id="DateRange.today" defaultMessage="сегодня"/>
                </button>
                <button className={classes.PresetBtn} onClick={function () { setPreset(setYesterday); }}>
                    <FormattedMessage id="DateRange.yesterday" defaultMessage="вчера"/>
                </button>
                <button className={classes.PresetBtn} onClick={function () { setPreset(setWeek); }}>
                    <FormattedMessage id="DateRange.days7" defaultMessage="7 дней"/>
                </button>
                <button className={classes.PresetBtn} onClick={function () { setPreset(setMonth); }}>
                    <FormattedMessage id="DateRange.days30" defaultMessage="30 дней"/>
                </button>
                <button className={classes.PresetBtn} onClick={function () { setPreset(setHalfYear); }}>
                    <FormattedMessage id="DateRange.halfYear" defaultMessage="полгода"/>
                </button>

                <div className={classes.SidebarDivider}/>

                <div className={classes.TimeSection}>
                    <label className={classes.TimeLabel}>
                        <FormattedMessage id="DateRange.from" defaultMessage="С:"/>
                    </label>
                    <input
                        type="time"
                        className={classes.TimeInput}
                        value={startTime}
                        onChange={handleStartTimeChange}
                    />
                </div>
                <div className={classes.TimeSection}>
                    <label className={classes.TimeLabel}>
                        <FormattedMessage id="DateRange.to" defaultMessage="По:"/>
                    </label>
                    <input
                        type="time"
                        className={classes.TimeInput}
                        value={endTime}
                        onChange={handleEndTimeChange}
                    />
                </div>
            </div>

            <div className={classes.Calendars}>
                {isMobile ? renderMonth(rightMonth, true, true) : (
                    <React.Fragment>
                        {renderMonth(left, true, false)}
                        {renderMonth(rightMonth, false, true)}
                    </React.Fragment>
                )}
            </div>
        </React.Fragment>
    );

    var panel = isMobile ? (
        <React.Fragment>
            <div className={classes.SheetBackdrop} onClick={closePanel}/>
            <div ref={dropdownRef} className={classes.Dropdown + " " + classes.Sheet}>
                <div className={classes.SheetHeader}>
                    <span className={classes.SheetTitle}>
                        <FormattedMessage id="DateRange.title" defaultMessage="Период"/>
                    </span>
                    <button type="button" className={classes.SheetClose} onClick={closePanel}>
                        <Icon icon="close" size="16px"/>
                    </button>
                </div>
                {panelBody}
            </div>
        </React.Fragment>
    ) : (
        <div
            ref={dropdownRef}
            className={classes.Dropdown}
            style={{top: dropdownPos.top, left: dropdownPos.left}}
        >
            {panelBody}
        </div>
    );

    return (
        <div className={classes.Wrap} ref={wrapRef}>
            <div
                ref={triggerRef}
                className={classes.Trigger + (isOpen ? " " + classes.TriggerActive : "")}
                onClick={function () {
                    if (!isOpen && !isMobile && triggerRef.current) {
                        var rect = triggerRef.current.getBoundingClientRect();
                        var panelWidth = 532;
                        var pos = rect.left;
                        if (pos + panelWidth > window.innerWidth) {
                            pos = window.innerWidth - panelWidth - 8;
                        }
                        if (pos < 8) pos = 8;
                        setDropdownPos({top: rect.bottom + 4, left: pos});
                    }
                    setIsOpen(!isOpen);
                }}
            >
                <span className={classes.TriggerLabel}>
                    <FormattedMessage id="DateRange.title" defaultMessage="Период"/>:
                </span>
                {displayText ? (
                    <span className={classes.TriggerValue}>{displayText}</span>
                ) : (
                    <span className={classes.TriggerPlaceholder}>
                        <FormattedMessage id="DateRange.placeholder" defaultMessage="Выберите период"/>
                    </span>
                )}
                <span className={classes.TriggerArrow}>{isOpen ? "▲" : "▼"}</span>
            </div>

            {isOpen && ReactDOM.createPortal(panel, document.body)}
        </div>
    );
}

export default DateRange;



// WEBPACK FOOTER //
// ./src/Components/Utils/DateRange/index.js